import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { getPublicAvatarUrl } from '../../utils/publicAvatarUtils';
import { getAvatarFallbackText } from '../../utils/publicAvatarUtils';
import avatarCacheSystem, { getCachedAvatarUrl } from '../../utils/avatarCacheSystem';

/**
 * 최적화된 아바타 컴포넌트
 * - 지속적 캐싱 (localStorage + 메모리)
 * - 로딩 중 그라데이션 애니메이션
 * - 솔리드 배경 없이 투명한 로딩 상태
 */
const OptimizedAvatar = ({ 
  userID, 
  username, 
  size = 60, 
  fallbackUrl = null,
  style = {},
  onLoadChange = null,
  ...props 
}) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const loadAvatar = async () => {
      if (!userID) {
        setImageSrc(fallbackUrl);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(false);

      try {
        // 🎯 1단계: 통합 캐시에서 확인 (원본 우선)
        const retinaSize = Math.ceil(size * 2); // 레티나 지원
        let cachedUrl = getCachedAvatarUrl(userID, retinaSize);

        if (cachedUrl) {
          // 캐시된 이미지가 있으면 즉시 사용
          setImageSrc(cachedUrl);
          setLoading(false);
          setError(false);
          if (onLoadChange) {
            onLoadChange(true);
          }
          return;
        }

        // 🚀 2단계: 점진적 로딩 전략
        // 작은 이미지 먼저 로드 (빠른 UX)
        const quickLoadSize = Math.min(retinaSize, 160); // 최대 160px로 제한
        const quickUrl = getPublicAvatarUrl(userID, quickLoadSize);
        
        // 작은 이미지 로드
        const quickImg = new Image();
        quickImg.crossOrigin = 'anonymous';
        
        quickImg.onload = () => {
          setImageSrc(quickUrl);
          setLoading(false);
          if (onLoadChange) {
            onLoadChange(true);
          }
          
          // 🎯 3단계: 백그라운드에서 원본/고해상도 이미지 로드
          if (retinaSize > quickLoadSize) {
            loadHighResolution(userID, retinaSize);
          }
        };
        
        quickImg.onerror = () => {
          // 작은 이미지도 실패하면 원본 캐시에서 preload 시도
          tryOriginalFromCache(userID, retinaSize);
        };
        
        quickImg.src = quickUrl;

      } catch (error) {
        console.error('Avatar loading failed:', error);
        setError(true);
        setImageSrc(fallbackUrl);
        setLoading(false);
      }
    };

    // 고해상도 이미지 백그라운드 로드
    const loadHighResolution = async (userID, targetSize) => {
      try {
        // 통합 캐시 시스템을 통해 원본 이미지 preload
        await avatarCacheSystem.preloadAvatar(userID);
        
        // 원본이 캐시되면 고해상도 URL 가져오기
        const highResUrl = getCachedAvatarUrl(userID, targetSize);
        
        if (highResUrl && highResUrl !== imageSrc) {
          // 부드러운 전환을 위해 이미지 검증 후 교체
          const img = new Image();
          img.crossOrigin = 'anonymous';
          
          img.onload = () => {
            setImageSrc(highResUrl);
            console.log(`✨ Upgraded to high-res avatar: ${userID} (${targetSize}px)`);
          };
          
          img.onerror = () => {
            console.warn(`Failed to load high-res avatar: ${userID}`);
          };
          
          img.src = highResUrl;
        }
      } catch (error) {
        console.warn('High-resolution avatar loading failed:', error);
      }
    };

    // 원본 캐시에서 시도
    const tryOriginalFromCache = async (userID, targetSize) => {
      try {
        // 원본 이미지 preload 시도
        const originalUrl = await avatarCacheSystem.preloadAvatar(userID);
        
        if (originalUrl) {
          const finalUrl = targetSize === 512 ? originalUrl : getPublicAvatarUrl(userID, targetSize);
          setImageSrc(finalUrl);
          setLoading(false);
          setError(false);
          if (onLoadChange) {
            onLoadChange(true);
          }
        } else {
          throw new Error('Failed to load from cache');
        }
      } catch (error) {
        console.error('Original cache loading failed:', error);
        setError(true);
        setImageSrc(fallbackUrl);
        setLoading(false);
        if (onLoadChange) {
          onLoadChange(true);
        }
      }
    };

    loadAvatar();
  }, [userID, size, fallbackUrl]);

  const fallbackText = getAvatarFallbackText(username);

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        overflow: 'hidden',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.4,
        fontWeight: 600,
        color: '#7A8591',
        backgroundColor: 'transparent',
        // 테두리 원 그려지는 효과
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -2,
          left: -2,
          right: -2,
          bottom: -2,
          borderRadius: '50%',
          border: '2px solid #E4E7EA',
          opacity: !loading && style?.opacity === 1 ? 1 : 0,
          transform: !loading && style?.opacity === 1 ? 'scale(1)' : 'scale(0.8)',
          transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
          animation: !loading && style?.opacity === 1 ? 'borderDraw 0.8s ease-out forwards' : 'none',
        },
        '@keyframes borderDraw': {
          '0%': {
            clipPath: 'polygon(50% 0%, 50% 0%, 50% 50%, 50% 50%)',
            opacity: 0,
          },
          '25%': {
            clipPath: 'polygon(50% 0%, 100% 0%, 50% 50%, 50% 50%)',
            opacity: 0.3,
          },
          '50%': {
            clipPath: 'polygon(50% 0%, 100% 0%, 100% 100%, 50% 50%)',
            opacity: 0.6,
          },
          '75%': {
            clipPath: 'polygon(50% 0%, 100% 0%, 100% 100%, 0% 100%)',
            opacity: 0.8,
          },
          '100%': {
            clipPath: 'polygon(50% 0%, 100% 0%, 100% 100%, 0% 100%, 0% 0%)',
            opacity: 1,
          },
        },
        ...style
      }}
      {...props}
    >
      {imageSrc && !loading && (
        <Box
          component="img"
          src={imageSrc}
          alt={username}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: '50%',
          }}
        />
      )}
      {!imageSrc && !loading && fallbackText}
    </Box>
  );
};

export default OptimizedAvatar;